import { ListOrdered, Search } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';

import { PermissionGate } from '@/auth/PermissionGate';
import { usePageEnter } from '@/lib/motion';
import { cn } from '@/lib/utils';

import { ApoyosContenido } from '../biblioteca/CasosClinicos';
import { Cargando, ErrorCarga, VisorLaminas } from '../curso/componentes';

import { MANUALES, useGuardarAvanceManual, useManual, type ManualViewer } from './queries';

// Menú de manuales del legacy (`inicio/manuales/header.php`).
const MENU_MANUALES = [
  { slug: 'algoritmos', rotulo: 'Algoritmos' },
  { slug: 'ultra-resumenes', rotulo: 'Ultraresúmenes' },
  { slug: 'clasificaciones-y-tablas', rotulo: 'Clasificaciones y tablas' },
];

const sinAcentos = (s: string) =>
  s
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();

/** Visor único de los 5 manuales de láminas (antes, 5 carpetas PHP con dos visores distintos). */
export function ManualPage({ slug }: { slug: string }) {
  const m = MANUALES[slug];
  if (!m) return null;
  return (
    <PermissionGate permiso={m.permiso} modulo={m.titulo}>
      <Manual slug={slug} />
    </PermissionGate>
  );
}

function Manual({ slug }: { slug: string }) {
  const { data, error, isPending, refetch } = useManual(slug);
  if (isPending) return <Cargando alto="h-96" />;
  if (error) return <ErrorCarga error={error} />;
  return <Visor key={slug} slug={slug} data={data} onExpirada={() => refetch()} />;
}

function Visor({
  slug,
  data,
  onExpirada,
}: {
  slug: string;
  data: ManualViewer;
  onExpirada: () => unknown;
}) {
  const [params, setParams] = useSearchParams();
  const total = data.slides.length;
  // Abre en la lámina pedida (`?lamina=` desde la búsqueda) o en la última vista.
  const [actual, setActual] = useState(() => {
    const pedida = data.slides.findIndex((s) => String(s.id) === params.get('lamina'));
    return (pedida >= 0 ? pedida : data.savedSlide) + 1;
  });
  const [filtro, setFiltro] = useState('');
  const [indice, setIndice] = useState(false);
  const guardar = useGuardarAvanceManual(slug);
  const espera = useRef<number | undefined>(undefined);
  const lista = useRef<HTMLOListElement>(null);
  const root = useRef<HTMLDivElement>(null);
  usePageEnter(root, [slug]);

  const ir = (n: number) => {
    const destino = Math.min(Math.max(n, 1), total);
    setActual(destino);
    setIndice(false);
    if (params.get('lamina')) setParams({}, { replace: true });
    window.clearTimeout(espera.current);
    espera.current = window.setTimeout(() => {
      guardar.mutate(destino - 1);
    }, 800);
  };

  // Mantiene visible en el índice la lámina actual.
  useEffect(() => {
    lista.current
      ?.querySelector<HTMLElement>('[aria-current="true"]')
      ?.scrollIntoView({ block: 'nearest' });
  }, [actual]);

  const visibles = useMemo(() => {
    const f = sinAcentos(filtro.trim());
    return data.slides.filter((s) => !f || sinAcentos(s.name).includes(f));
  }, [data.slides, filtro]);
  const lamina = data.slides[actual - 1];

  const Indice = (
    <div className="flex h-full flex-col">
      <label className="relative mb-3 block">
        <Search size={16} className="absolute top-1/2 left-3 -translate-y-1/2 text-texto-3" />
        <input
          className="input-vidrio w-full pl-9"
          placeholder="Buscar en el índice"
          value={filtro}
          onChange={(e) => setFiltro(e.target.value)}
        />
      </label>
      <ol ref={lista} className="-mx-2 flex-1 space-y-0.5 overflow-y-auto px-2">
        {visibles.map((s) => (
          <li key={s.id}>
            <button
              type="button"
              aria-current={s.number === actual ? 'true' : undefined}
              onClick={() => ir(s.number)}
              className={cn(
                'flex w-full gap-2 rounded-lg px-2 py-1.5 text-left text-sm transition-colors hover:bg-white/10',
                s.heading && 'mt-2 font-extrabold text-azul-texto uppercase',
                s.number === actual && 'bg-azul-600 !text-white',
              )}
            >
              <span className="w-7 shrink-0 text-right text-xs tabular opacity-70">{s.number}</span>
              <span>{s.name}</span>
            </button>
          </li>
        ))}
        {visibles.length === 0 && <li className="px-2 text-sm text-texto-3">Sin coincidencias.</li>}
      </ol>
    </div>
  );

  return (
    <div ref={root} className="mx-auto max-w-screen-2xl space-y-5">
      <nav aria-label="Manuales" className="flex flex-wrap gap-2">
        {MENU_MANUALES.map((m) => (
          <Link
            key={m.slug}
            to={`/manuales/${m.slug}`}
            className="btn-modulo text-xs no-underline"
            aria-current={m.slug === slug ? 'page' : undefined}
          >
            {m.rotulo}
          </Link>
        ))}
      </nav>
      <header className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="titulo-seccion">
          <span className="subrayado-verde">{data.title}</span>
        </h1>
        <button
          type="button"
          className="btn-cs btn-contorno lg:hidden"
          onClick={() => setIndice((v) => !v)}
          aria-expanded={indice}
        >
          <ListOrdered size={18} /> Índice
        </button>
      </header>
      {total === 0 ? (
        <p className="panel-vidrio text-center text-texto-2">Este manual aún no tiene láminas.</p>
      ) : (
        <div className="grid gap-6 lg:grid-cols-[18rem_minmax(0,1fr)]">
          {/* Tarjeta «Contenido Temático» del legacy: cabecera con el degradado de marca. */}
          <aside
            className={cn(
              'panel-vidrio max-h-[75vh] flex-col overflow-hidden p-0 lg:sticky lg:top-24 lg:flex',
              indice ? 'flex' : 'hidden',
            )}
          >
            <h2 className="bg-marca px-4 py-2 text-center font-extrabold text-white">
              Contenido Temático
            </h2>
            <div className="min-h-0 flex-1 p-4">{Indice}</div>
          </aside>
          <div className="space-y-4">
            {lamina && (
              <p className="text-sm text-texto-2">
                <strong className="text-texto">{lamina.name}</strong>
                {lamina.english && <span className="insignia ml-2">Inglés</span>}
              </p>
            )}
            <VisorLaminas
              titulo={data.title}
              contenido={['manual', lamina?.id ?? 0]}
              laminas={data.slides.map((s) => ({
                clave: s.id,
                imageUrl: s.imageUrl,
                nombre: s.name,
              }))}
              actual={actual}
              onCambio={ir}
              onExpirada={onExpirada}
              miniaturas={false}
            />
            {lamina && <ApoyosContenido key={lamina.id} tipo="manuals" id={lamina.id} />}
          </div>
        </div>
      )}
    </div>
  );
}

/** Para el router: el slug sale de la ruta `/manuales/:slug`. */
export function ManualRuta() {
  const { slug = '' } = useParams();
  return <ManualPage slug={slug} />;
}
